import { RtmMessage } from 'agora-rtm-sdk';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Image } from 'react-bootstrap';
import { Debug } from '../../../../common/utils/debugLogs';
import { convertTime, getLevelColor } from '../../../../common/utils/utils';
import {
  usePostFollowMutation,
  useGetLiveCoinMutation,
  useGetPersonalDataMutation,
} from '../../../../services/api/apiProvider';
import { LiveProps } from '../../LiveContainer';
import './LiveRecord.scss';
import officalLiverIcon from '../../../../assets/svg/officialliver_icon.svg';
import { ImageCustom } from '../../../../common/components/ImageCustom';
import CardHeader from 'react-bootstrap/esm/CardHeader';
import { MessageType } from '../../types/messageType';
import { MessageUser } from '../../models/messageUser';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../liveSlice';
import {
  updateFollowing,
  updateUserData,
} from '../../../profile/slices/profilePageSlice';
import { AppState } from '../../../../store/store';
import { reproio } from '../../../../common/utils/reproio';

export const LiveRecord = (props: LiveProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // live score
  const [score, setScore] = useState<number | undefined>(
    props.liveState.liveData?.score,
  );
  const { isFollowing } = useSelector(
    (state: AppState) => state.profilePageState,
  );
  // live time
  const [liveTimeSec, setLiveTimeSec] = useState<number>(0);

  // live coin
  const [coin, setCoin] = useState<number | undefined>();
  const [getLiveCoin] = useGetLiveCoinMutation();

  // follow liver
  const [postFollow] = usePostFollowMutation();
  const [getLiverPersonalData] = useGetPersonalDataMutation();

  /**
   * Fetch live coin data from API.
   */
  const fetchCoinData = () => {
    if (props.liveState.liveData) {
      getLiveCoin(props.liveState.liveData.id)
        .unwrap()
        .then((data) => {
          setCoin(data.coin);
        });
    }
  };

  /**
   * Fetch live coin data from API.
   */
  const fetchLiverPersonalData = async () => {
    if (props.liveState.liveData) {
      const liverId = props.liveState.liveData.user.id;
      const liverData = await getLiverPersonalData(liverId).unwrap();
      if (liverData) {
        dispatch(updateFollowing(liverData.followStatus === 'following'));
      }
    }
  };

  /**
   * Get live time second
   */
  const getLiveTime = (): number => {
    if (props.liveState.liveData) {
      const now = new Date();
      const startTime = new Date(props.liveState.liveData.startDateTime);
      const time = Math.floor((now.getTime() - startTime.getTime()) / 1000);
      return time;
    } else {
      return 0;
    }
  };

  /**
   * Listen RTM message from joined channel
   * Handle messages: Notify score, Notify coin.
   */
  const listenChannelMessage = () => {
    props.liveState.rtmEngine?.on(
      'ChannelMessage',
      (message: RtmMessage): void => {
        if (message.messageType === 'TEXT') {
          const content = JSON.parse(message.text);
          // New score
          if (content.type === MessageType.NOTIFY_SCORE) {
            const newScore = content.value.score as number;
            if (!newScore) {
              return;
            }

            Debug.log('NEW SCORE ' + newScore);
            setScore(newScore);
          }

          // New coin
          if (content.type === MessageType.NOTIFY_BERRY_COIN) {
            const newCoin = content.value.coin as number;
            if (!newCoin) {
              return;
            }

            Debug.log('NEW COIN ' + newCoin);
            setCoin(newCoin);
          }

          // Level Up
          if (content.type === MessageType.NOTIFY_LIVER_LEVEL_UP) {
            try {
              const user = content.value as MessageUser;
              props.updateLiverLevel(user);
            } catch (error) {
              Debug.log(`Fail to parse notify message: ${error}`);
            }
          }
        }
      },
    );
  };

  /**
   * Side effect for counting of live time
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (props.liveState.isInLive && props.liveState.liveData) {
      interval = setInterval(() => {
        setLiveTimeSec(getLiveTime());
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  });

  useEffect(() => {
    if (props.liveState.isInLive && props.liveState.liveData) {
      setLiveTimeSec(getLiveTime());
      setScore(props.liveState.liveData.score);
      fetchCoinData();
      if (props.liveState.role === 'audience') {
        fetchLiverPersonalData();
      }
      listenChannelMessage();
    }
  }, [props.liveState.isInLive]);

  const handleFollowClick = async () => {
    reproio('track', 'tap follow button');
    if (!props.liveState.liveData) {
      return;
    }
    const userId = props.liveState.liveData.user.id;
    const response = await postFollow(userId).unwrap();
    if (response) {
      dispatch(updateFollowing(true));
    }
  };

  const handleShowModalProfile = async (id: string) => {
    const data = await getLiverPersonalData(id).unwrap();
    if (data) {
      dispatch(updateUserData(data));
    }
    dispatch(actions.toggleProfileModal(true));
  };

  return (
    <div className="live-record">
      <Card className="live-record__container box-shadow-2">
        {/* Header - Liver Info */}
        {props.liveState.liveData !== undefined ? (
          <Card.Header>
            <li className="d-flex live-record__liver-info">
              <div
                onClick={() => {
                  if (props.liveState.liveData) {
                    handleShowModalProfile(props.liveState.liveData.user.id);
                  }
                }}
                className="d-flex flex-column"
              >
                <div className="liver-info__avatar">
                  <ImageCustom
                    src={props.liveState.liveData?.user?.picture?.uri}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center">
                {/* Offical badge */}
                {props.liveState.liveData?.user.isCertifiedLiver ? (
                  <Image
                    src={officalLiverIcon}
                    className="liver-info__badge"
                  ></Image>
                ) : (
                  <></>
                )}

                {/* LoginId */}
                <div className="d-flex flex-column">
                  <div
                    className="liver-info__name"
                    style={{
                      color: getLevelColor(props.liveState.liverLv),
                    }}
                  >
                    {props.liveState.liveData?.user.loginId}
                  </div>
                </div>

                {/* Level */}
                <div className="d-flex flex-column">
                  <span
                    className="liver-info__level"
                    style={{
                      backgroundColor: getLevelColor(props.liveState.liverLv),
                    }}
                  >{`Lv.${props.liveState.liverLv}`}</span>
                </div>
              </div>

              {/** Follow button*/}
              {props.liveState.role === 'audience' && isFollowing === false && (
                <div
                  className="live-record__follow-btn"
                  onClick={handleFollowClick}
                >
                  フォロー
                </div>
              )}
            </li>
          </Card.Header>
        ) : (
          <CardHeader style={{ minHeight: '57px' }}></CardHeader>
        )}

        {/* Live record stats */}
        <Card.Body className="live-record__body">
          <Card.Text className="live-record__property">
            <label>{t('LiveTime')}</label>
            <span className="live-record__stat">
              {liveTimeSec ? convertTime(liveTimeSec) : ''}
            </span>
          </Card.Text>
          <Card.Text className="live-record__property">
            <label>{t('Diamond')}</label>
            <span className="live-record__stat">
              {coin?.toLocaleString() ?? ''}
            </span>
          </Card.Text>
          <Card.Text className="live-record__property">
            <label>{t('Score')}</label>
            <span className="live-record__stat">
              {score?.toLocaleString() ?? ''}
            </span>
          </Card.Text>
        </Card.Body>
      </Card>
    </div>
  );
};
